import { CODEX_BARS, CODEX_BARS_BOSS } from '../data/codex';
import { CODEX_ALL, codexScale } from '../data/codex-list';
import { playerRef } from './codex-player';
/* ============================================================
   右欄的數值條：中位數刻度 + 相對你
   ============================================================

   舊版的條沒有任何基準：「速度 1.3」的條佔一半，玩家不知道那是相對於誰
   （實看四種尺寸量出來的毛病之一）。

   B3 給兩個參照，而且**分開講**（理由見 `codex-player.ts`：玩家的值不在
   敵人的量尺上，畫在條上會落到框外）：

   1. **條上一道中位數刻度** —— 這隻在同類裡算快的還是慢的。
      Boss 只跟 Boss 比（`CODEX_BARS_BOSS` 是另一組上限，同一條老規矩）。
   2. **值後面一句「你的 N%」** —— 這隻對我來說快不快、遠不遠。
      沒有玩家對應值的欄位（預備）不給這一句，不硬湊。

   ⚠️ 中位數是**算出來的不是寫死的**。原型一直在長（這一輪之內 Boss 就從
   23 隻變 28 隻），寫死的中位數會在下一次移植之後靜靜地說謊。 */

/** 這一隻該用哪一組條。Boss 只跟 Boss 比。 */
export function barsFor(T) { return T && T.boss ? CODEX_BARS_BOSS : CODEX_BARS; }

var MED: any = { mob: null, boss: null };

/** 全體中位數。**分母是造型表的全部**（含開發模式才列得出來的 Boss）——
    中位數是「這一批東西」的性質，不是「你解鎖了多少」的性質。 */
function medians(boss) {
  var key = boss ? 'boss' : 'mob';
  if (MED[key]) return MED[key];
  var bars = boss ? CODEX_BARS_BOSS : CODEX_BARS, out = {};
  for (var b = 0; b < bars.length; b++) {
    var k = bars[b].k, vals: number[] = [];
    for (var i = 0; i < CODEX_ALL.length; i++) {
      var T = CODEX_ALL[i];
      if (!!T.boss !== !!boss) continue;
      var v = T[k];
      if (typeof v === 'number' && isFinite(v)) vals.push(v);
    }
    vals.sort(function (a, c) { return a - c; });
    out[k] = vals.length ? vals[vals.length >> 1] : 0;
  }
  MED[key] = out;
  return out;
}

/** 這一隻在**戰場上**的體型倍率 —— 建表時算好的（`data/codex-list.ts`）。

    ⚠️ 不要在這裡重算：寫死 `BODY_K` 的話 KAISER 的欄位會寫「48.0m
    ／你的 1090%」，而玩家在戰場上看到的是 18 公尺、3.6 倍高 ——
    數字說的跟眼睛看到的是兩件事（2026-08-03 就是這樣壞的）。 */
function bodyK(T) { return codexScale(T); }

/** 值要怎麼寫成字。體型類換算成世界公尺，時間類補秒。 */
function fmt(k, v, bk) {
  if (k === 'h' || k === 'w') return (v * bk).toFixed(1) + 'm';
  if (k === 'tTel') return v.toFixed(2) + 's';
  return v.toFixed(1);
}

/** 相對玩家的一句話。回空字串代表這一欄沒有玩家對應值。 */
function vsYou(k, v, bk) {
  var R: any = playerRef();
  var p = R[k];
  if (!p) return '';
  // 體型要比**世界尺寸**，兩邊的倍率不一樣（見 `bodyK`）
  var pct = Math.round((k === 'h' || k === 'w' ? v * bk / p : v / p) * 100);
  var word = k === 'rng' ? '射程' : k === 'h' ? '體型' : '速度';
  return '你的 <b>' + pct + '%</b> ' + word;
}

/** 一條。`lock` 為真時整條塗銷（版面高度不變，見 `codex-data.ts`）。 */
export function barHTML(B, v, med, lock, bk?) {
  bk = bk || codexScale(null);
  var k = Math.max(0, Math.min(1, v / B.max));
  var mk = Math.max(0, Math.min(1, med / B.max));
  if (lock) {
    return '<div class="cbar lock"><span>' + B.n + '</span>'
      + '<div><i style="width:100%"></i></div><em>███</em></div>';
  }
  var vs = vsYou(B.k, v, bk);
  return '<div class="cbar"><span>' + B.n + '</span>'
    + '<div><i style="width:' + (k * 100).toFixed(1) + '%"></i>'
    /* 中位數刻度畫在條**裡面**，不是旁邊 —— 旁邊的話兩條窄欄一擠就對不齊，
       而對不齊的刻度比沒有刻度更糟。 */
    + '<u style="left:' + (mk * 100).toFixed(1) + '%"></u></div>'
    + '<em>' + fmt(B.k, v, bk) + '</em></div>'
    + (vs ? '<div class="cbar-vs">' + vs + '</div>' : '');
}

/** 整組。`lock` 為真時每一條都塗銷 —— **條數與高度不變**，
    「這裡有四個數字你還不知道」本身就是情報。 */
export function barsHTML(T, lock) {
  var bars = barsFor(T), med = medians(!!T.boss), h = '', bk = bodyK(T);
  for (var i = 0; i < bars.length; i++) h += barHTML(bars[i], T[bars[i].k], med[bars[i].k], lock, bk);
  if (!lock) {
    h += '<div class="cbar-note">刻度＝同類中位數'
      + (T.boss ? '（BOSS 只跟 BOSS 比）' : '') + '</div>';
  }
  return h;
}
